import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { useLanguage } from "@/context/LanguageContext";
import { interp } from "@/i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple, robust email regex — mirror of the backend check.
const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

const EMAIL_MAX = 45;
const MESSAGE_MAX_CHARS = 400;
const MESSAGE_MAX_LINES = 9;

const ContactPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const [errors, setErrors] = useState({ email: "", message: "" });

  const lineCount = useMemo(() => message.split("\n").length, [message]);

  const handleEmailChange = (e) => {
    const v = e.target.value.slice(0, EMAIL_MAX);
    setEmail(v);
    if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
  };

  const handleMessageChange = (e) => {
    let v = e.target.value;
    // Clamp to max lines by dropping any lines past the ninth.
    const parts = v.split("\n");
    if (parts.length > MESSAGE_MAX_LINES) v = parts.slice(0, MESSAGE_MAX_LINES).join("\n");
    if (v.length > MESSAGE_MAX_CHARS) v = v.slice(0, MESSAGE_MAX_CHARS);
    setMessage(v);
    if (errors.message) setErrors((prev) => ({ ...prev, message: "" }));
  };

  const validate = () => {
    const next = { email: "", message: "" };
    if (!EMAIL_RE.test(email) || email.length > EMAIL_MAX) next.email = t.contact.errEmail;
    const trimmed = message.trim();
    if (!trimmed || trimmed.length > MESSAGE_MAX_CHARS || lineCount > MESSAGE_MAX_LINES) {
      next.message = t.contact.errMessage;
    }
    setErrors(next);
    return !next.email && !next.message;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    if (!validate()) return;

    setStatus("sending");
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), message: message.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("sent");
      toast.success(t.contact.sent);
      setEmail("");
      setMessage("");
    } catch (err) {
      console.error(err);
      setStatus("idle");
      toast.error(t.contact.failed);
    }
  };

  return (
    <main
      data-testid="contact-page"
      className="relative min-h-[calc(100vh-72px)] max-w-3xl mx-auto px-6 md:px-12 py-14 md:py-20"
    >
      {/* Italic centered quote */}
      <motion.section
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-center mb-14"
      >
        <p
          data-testid="contact-quote"
          className="font-serif italic text-ivory text-2xl md:text-3xl leading-snug"
        >
          {t.contact.quote}
        </p>
      </motion.section>

      {/* Contact form box */}
      <section className="rounded-md border border-gold/40 bg-oxblood-surface/70 p-6 md:p-10">
        <h1
          data-testid="contact-title"
          className="text-lg md:text-xl tracking-[0.35em] uppercase text-[#14103A] font-bold mb-8 text-center"
        >
          ✧ {t.contact.title}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
          {/* Email */}
          <div>
            <label
              htmlFor="c-email"
              className="block text-sm tracking-[0.25em] uppercase text-gold font-bold mb-2"
            >
              {t.contact.emailLabel}
            </label>
            <input
              id="c-email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              maxLength={EMAIL_MAX}
              placeholder={t.contact.emailPlaceholder}
              autoComplete="email"
              data-testid="contact-email-input"
              className="w-full bg-oxblood-surface border border-gold/25 rounded-md px-4 py-3 font-serif text-ivory placeholder-ivory/30 focus:outline-none focus:border-gold/70 transition-colors duration-300"
            />
            <div className="mt-1 flex justify-between items-center">
              <p className="text-xs text-ivory/40 font-serif italic">
                {email.length}/{EMAIL_MAX}
              </p>
              {errors.email && (
                <p className="text-sm text-red-300 font-serif italic" data-testid="contact-email-error">
                  {errors.email}
                </p>
              )}
            </div>
          </div>

          {/* Message */}
          <div>
            <label
              htmlFor="c-message"
              className="block text-sm tracking-[0.25em] uppercase text-gold font-bold mb-2"
            >
              {t.contact.messageLabel}
            </label>
            <textarea
              id="c-message"
              value={message}
              onChange={handleMessageChange}
              rows={9}
              maxLength={MESSAGE_MAX_CHARS}
              placeholder={t.contact.messagePlaceholder}
              data-testid="contact-message-input"
              className="w-full resize-none bg-oxblood-surface border border-gold/25 rounded-md px-4 py-3 font-serif text-ivory placeholder-ivory/30 focus:outline-none focus:border-gold/70 transition-colors duration-300"
            />
            <div className="mt-1 flex justify-between items-center">
              <p className="text-xs text-ivory/40 font-serif italic">
                {interp(t.contact.messageCount, { a: message.length, b: lineCount })}
              </p>
              {errors.message && (
                <p className="text-sm text-red-300 font-serif italic" data-testid="contact-message-error">
                  {errors.message}
                </p>
              )}
            </div>
          </div>

          {/* Send button */}
          <div className="pt-2 flex justify-center">
            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="contact-send-btn"
              className="inline-flex items-center gap-3 px-10 py-4 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-base md:text-lg tracking-[0.35em] uppercase font-bold disabled:opacity-40"
            >
              {status === "sending" ? t.contact.sending : t.contact.send}
              <span className="text-2xl leading-none">→</span>
            </button>
          </div>

          {status === "sent" && (
            <p
              data-testid="contact-sent-msg"
              className="text-center text-ivory font-serif italic pt-2"
            >
              ✧ {t.contact.sent}
            </p>
          )}
        </form>
      </section>

      <div className="mt-16 pt-8 border-t border-gold/10 text-center">
        <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ivory/40">{t.footer}</p>
      </div>
    </main>
  );
};

export default ContactPage;
